import { fieldJournal, type Biome, type GameState, type GrowthStage } from '@thicket/core';
import { html, nothing } from 'lit-html';
import { art, iconFit } from '../garden/art/index.js';
import { app, BIOME_NAMES, update } from './app.js';
import { streakText, treeName } from './names.js';

/** The stage after each one, as the journal names it. */
const NEXT: Record<string, string> = { seed: 'small', small: 'medium', medium: 'large', large: 'mega' };

const treeIcon = (species: string, stage: string, sparse: boolean, biome: Biome) =>
  iconFit(`journal:${species}:${stage}:${sparse}:${biome}`, (ctx) =>
    art.tree(ctx, { x: 16, y: 31, species, stage: stage as GrowthStage, sparse, biome, seed: 3, tick: 0, reduce: true }), 32, 32);

/** Every tree in every biome, plus what's been collected. */
export function journalView(s: GameState) {
  const rules = app.store.rules;
  const trees = fieldJournal(s, rules);
  const bouquets = s.inventory.find((i) => i.type === 'decoration' && i.kind === 'bouquet')?.quantity ?? 0;
  const pressed = s.inventory.filter((i) => i.type === 'pressedFlower');
  const textOf = (id: string | null) => s.tasks.find((t) => t.id === id)?.text ?? 'A wildflower';
  return html`
    <section id="field-journal">
      <p class="group-title">${trees.length} tree${trees.length === 1 ? '' : 's'}</p>
      <ul class="list">${trees.length ? trees.map((t) => {
        const prev = t.stage === 'seed' ? 0 : rules.stageCounts[s.plants.find((p) => p.id === t.plantId)!.pace][['small', 'medium', 'large', 'mega'].indexOf(t.stage)]!;
        const pct = t.nextStageAt ? Math.round(((t.completions - prev) / (t.nextStageAt - prev)) * 100) : 100;
        const leaves = t.leaves === 'full' ? '' : t.leaves === 'fillingIn' ? ` · leaves filling in (${t.streak}/${rules.leafRecovery})` : ' · leaves resting';
        return html`<li class="task journal">
          <img class="thumb big" src=${treeIcon(t.species, t.stage, t.leaves !== 'full', t.biome ?? s.profile.starterBiome)} width="56" height="56" alt="">
          <button class="body" @click=${() => update(() => (app.sheet = { kind: 'task', taskId: t.taskId }))}>
            <span class="title">${t.text}</span>
            <span class="meta">${t.stage === 'seed' ? `${treeName(t.species, t.biome ?? s.profile.starterBiome)} seed` : `${t.stage} ${treeName(t.species, t.biome)}`} · ${t.biome ? BIOME_NAMES[t.biome] : 'not planted yet'} · ${streakText(t.streak)}${leaves}${t.paused ? ' · resting' : ''}</span>
            <span class="progress" aria-label=${t.nextStageAt ? `${t.completions} of ${t.nextStageAt} completions to the next stage` : 'Fully grown'}><i style=${`width:${Math.max(0, Math.min(100, pct))}%`}></i></span>
            <span class="meta">${t.nextStageAt ? `${t.nextStageAt - t.completions} more to a ${NEXT[t.stage]} ${treeName(t.species, t.biome ?? s.profile.starterBiome)}` : `Fully grown · ${t.completions} completions`}</span>
          </button></li>`;
      }) : html`<li class="empty">Every daily grows a tree. Add one from the Dailies tab.</li>`}</ul>
    </section>
    <p class="group-title">Collections</p>
    <section class="box">
      <p style="margin:0 0 8px">Bouquets: ${bouquets}</p>
      ${pressed.length ? html`<h3 style="margin-bottom:6px">Pressed flowers</h3><ul class="list">${pressed.map((p) =>
        html`<li class="task"><span class="body"><span class="title">${textOf(p.taskId)}</span><span class="meta">${p.kind} · ${p.day}</span></span></li>`)}</ul>`
        : html`<p class="empty" style="margin:0">Press a wildflower from the garden to keep it here.</p>`}
    </section>
    ${nothing}`;
}
